import React, { useCallback } from "react";

import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faChevronDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Emoji } from "emoji-mart";

import { navStore } from "~/nav";
import { Button, Tooltip } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";

import HomePanelGroupEllipsis from "../Home/Sections/HomePanelGroupEllipsis";

import type { HomePanelGroup_group$data as HomePanelGroupType } from "~/__generated__/HomePanelGroup_group.graphql";

import styles from "./PanelSection.module.css";

const getCollapseStoreKey = (name: string) => `groupCollapsed.${name}`;

interface PanelSectionProps extends React.HTMLProps<HTMLDivElement> {
	title: string;
	/** A snake_case emoji name. @todo add FontAwesome support  */
	icon?: string | IconDefinition;
	onPlusClick?: () => void;
	plusButtonTooltip?: string;
	headerButtonText?: string;
	headerButtonClick?: () => void;
	plusButtonTestId?: string;
	afterTitle?: React.ReactElement;
	beforePlusButton?: React.ReactElement;
	"data-test-id"?: string;
	/** Visually fades section to indicate you can't drop the current object into it */
	dropDisabledFade?: boolean;
	unread?: boolean;
	group?: HomePanelGroupType;
	isDragging?: boolean;
	hideHomePanelGroupEllipsis?: boolean;
	isCollapsable?: boolean;
}

const PanelSection = React.forwardRef<HTMLDivElement, PanelSectionProps>(
	(
		{
			title,
			icon,
			onPlusClick,
			plusButtonTooltip,
			headerButtonText,
			headerButtonClick,
			plusButtonTestId,
			afterTitle,
			beforePlusButton,
			children,
			"data-test-id": testId,
			className,
			dropDisabledFade,
			unread,
			group,
			isDragging,
			hideHomePanelGroupEllipsis,
			isCollapsable,
			...rest
		},
		ref,
	) => {
		const shouldCollapse = isCollapsable ?? true;
		const collapseStoreKey = getCollapseStoreKey(title);
		const [isCollapsed, setIsCollapsed] = navStore.useState<boolean>(collapseStoreKey, false);
		const toggleCollapsed = () => {
			if (!shouldCollapse) return;
			setIsCollapsed(!isCollapsed);
		};

		const generatePlusButton = useCallback(
			() => (
				<IconButton
					icon={faPlus}
					onClick={onPlusClick}
					className={cx(styles.plusButton, {
						[styles.withHeaderLink]: Boolean(headerButtonText && headerButtonClick),
					})}
					data-test-id={plusButtonTestId}
				/>
			),
			[onPlusClick, headerButtonText, headerButtonClick, plusButtonTestId],
		);

		return (
			<div
				ref={ref}
				{...rest}
				className={cx(className, styles.panelSection, {
					[styles.panelCollapsed]: isCollapsed,
					[styles.panelCollapsedNoItemsUnread]: isCollapsed && !unread,
					[styles.dropDisabledFade]: dropDisabledFade,
					[styles.dragging]: isDragging,
					[styles.collapseCaretAlwaysVisible]: icon === undefined,
					[styles.notCollapsable]: !shouldCollapse,
				})}
				data-test-id={testId}
			>
				<div className={cx(styles.panelSectionHeader, { [styles.collapseOnHover]: shouldCollapse && !unread })}>
					<button className={styles.collapseButton} onClick={toggleCollapsed} title={title}>
						<span className={styles.sectionIconContainer}>
							{shouldCollapse && (
								<FontAwesomeIcon fixedWidth icon={faChevronDown} className={styles.collapseButtonCaret} />
							)}
							{unread && isCollapsed ? (
								<span className={styles.unreadDot} />
							) : (
								<>
									{icon &&
										(typeof icon === "string" ? (
											<span className={styles.emojiContainer}>
												<Emoji native size={12} emoji={icon} />
											</span>
										) : (
											<FontAwesomeIcon fixedWidth icon={icon} className={styles.sectionIcon} />
										))}
								</>
							)}
						</span>
						<h2>{title}</h2>
						{afterTitle && <>&ensp;{afterTitle}</>}
					</button>
					{headerButtonText && headerButtonClick && (
						<Button size="wide" variant="tertiary" onClick={headerButtonClick} className={styles.textButton}>
							{headerButtonText}
						</Button>
					)}
					{!isCollapsed && beforePlusButton}
					{group && !Boolean(hideHomePanelGroupEllipsis) && (
						<HomePanelGroupEllipsis group={group} triggerClassName={styles.groupKebabMenu} />
					)}
					{onPlusClick &&
						(plusButtonTooltip ? (
							<Tooltip content={plusButtonTooltip} placement="top">
								{generatePlusButton()}
							</Tooltip>
						) : (
							generatePlusButton()
						))}
				</div>
				<div className={styles.panelSectionContent}>{children}</div>
			</div>
		);
	},
);
PanelSection.displayName = "PanelSection";

export default PanelSection;
